import { createApi } from '@reduxjs/toolkit/query/react';

import { fetchBaseQueryWithTokenCheck } from '@shared/utils/api.utils';
import { serializeFiltersForApi } from '@shared/utils/findings/trace-findings.utils';

import { DEFAULT_API_PAGE_SIZE, KEEP_UNUSED_DATA_FOR } from '../api.constants';

import type {
  EventSourcesParams,
  EventSourcesResponse,
  FacetedFiltersResponse,
  FacetSearchArgs,
  FacetSearchQueryArgs,
  TraceFindingsResponse,
} from './findings-api.types';
import type { FilterFieldValue } from './findings.types';

const buildFacetSearchRequest = ({
  investigationId,
  filtersQuery,
  page = 0,
  size = DEFAULT_API_PAGE_SIZE,
}: FacetSearchQueryArgs) => {
  const serialized = serializeFiltersForApi(filtersQuery);
  const payload = {
    ...serialized,
    paging: {
      page,
      size,
    },
  };

  return {
    url: `/api/event/v2/facetSearch/${investigationId}`,
    method: 'POST' as const,
    body: payload,
  };
};

export const traceEventsApiSlice = createApi({
  reducerPath: 'traceEventsApi',
  baseQuery: fetchBaseQueryWithTokenCheck,
  endpoints: (builder) => {
    return {
      getFacetedFilters: builder.query<
        FacetedFiltersResponse,
        { investigationId: string; filtersQuery: Record<string, FilterFieldValue> }
      >({
        query: ({ investigationId, filtersQuery }) => {
          const payload = serializeFiltersForApi(filtersQuery);

          return {
            url: `/api/event/v2/facets/${investigationId}`,
            method: 'POST',
            body: payload,
          };
        },
      }),

      getFacetSearch: builder.infiniteQuery<TraceFindingsResponse, FacetSearchArgs, number>({
        infiniteQueryOptions: {
          initialPageParam: 0,
          getNextPageParam: (lastPage, allPages) => {
            const loadedCount = allPages.reduce((count, page) => count + page.data.length, 0);
            return loadedCount < lastPage.metadata.total_events ? allPages.length : undefined;
          },
        },
        query: ({ queryArg, pageParam }) =>
          buildFacetSearchRequest({ ...queryArg, page: pageParam, size: DEFAULT_API_PAGE_SIZE }),
        keepUnusedDataFor: KEEP_UNUSED_DATA_FOR,
      }),
      getFacetSearchExport: builder.query<TraceFindingsResponse, FacetSearchQueryArgs>({
        query: buildFacetSearchRequest,
        keepUnusedDataFor: KEEP_UNUSED_DATA_FOR,
      }),
      getEventSources: builder.query<EventSourcesResponse, EventSourcesParams>({
        query: ({ paging, linkageKey }) => ({
          url: `/api/event/v2/${linkageKey}/sourceEvents`,
          params: { page: paging.page, size: paging.size },
        }),
      }),
    };
  },
});

export const { useGetFacetedFiltersQuery, useGetFacetSearchInfiniteQuery, useGetEventSourcesQuery } =
  traceEventsApiSlice;
